import { useState, useEffect, useCallback } from 'react';

export interface SearchHistoryItem {
  query: string;
  type: 'semantic' | 'hybrid' | 'keyword';
  timestamp: number;
  resultCount: number;
}

const MAX_HISTORY_ITEMS = 20;
const STORAGE_KEY = 'analizaprawna-search-history';

export function useSearchHistory() {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load search history:', e);
    }
  }, []);

  const addToHistory = useCallback((item: Omit<SearchHistoryItem, 'timestamp'>) => {
    setHistory((prev) => {
      // Remove duplicate queries
      const filtered = prev.filter(
        (h) => !(h.query.toLowerCase() === item.query.toLowerCase() && h.type === item.type)
      );

      const newHistory = [
        { ...item, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_HISTORY_ITEMS);

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (e) {
        console.error('Failed to save search history:', e);
      }

      return newHistory;
    });
  }, []);

  const removeFromHistory = useCallback((timestamp: number) => {
    setHistory((prev) => {
      const newHistory = prev.filter((h) => h.timestamp !== timestamp);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (e) {
        console.error('Failed to save search history:', e);
      }
      return newHistory;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear search history:', e);
    }
  }, []);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
  };
}
